import { Column } from 'further-ui/components/Layout';
import { FC } from 'react';
import FormField from 'further-ui/components/FormField';
import Switch from 'further-ui/components/Switch';
import Table from 'further-ui/components/Table';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import type { Column as TableColumn } from 'further-ui/components/Table/types';
import { FundFullDeploymentTrigger } from 'further-types/fund-fully-deployed-log';
import { NavLink } from 'react-router-dom';
import {
  dateToLabel,
  getCompanyLabel,
  numberToCurrencyString,
} from 'further-ui/utils';
import { useDisclosure } from 'further-ui/hooks';
import { useFundFullyDeployed } from 'hooks/data/fund/useFundFullyDeployed';
import { createViewAllocationRoute } from 'adminConstants/routes';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type FullyDeployedLogData = Awaited<
  ReturnType<typeof useFundFullyDeployed>
>['fullyDeployedLog']['data'];
type Log = NonNullable<FullyDeployedLogData>['logs'][number];

type Props = {
  fundId: string;
  hasKiCertificate: boolean;
};

const FinalDeal: FC<Props> = ({ fundId, hasKiCertificate }) => {
  const { fullyDeployedLog, fullyDeployFund, undoFundDeployment } =
    useFundFullyDeployed({ fundId });

  const fullyDeployDisclosure = useDisclosure();
  const undoDeploymentDisclosure = useDisclosure();

  const canEditTranche = useHasPermissions([RolePermission.EditTranche]);

  const mutationInProgress =
    fullyDeployFund.isPending || undoFundDeployment.isPending;

  const isFullyDeployed = fullyDeployedLog.data?.fund?.fullyDeployed ?? false;
  const logs: Array<Log> = fullyDeployedLog.data?.logs ?? [];

  const columns: Array<TableColumn<Log>> = [
    {
      label: 'Date',
      key: 'date',
      render: (row) => dateToLabel(row.date, '-'),
    },
    {
      label: 'Action',
      key: 'action',
      render: (row) => {
        if (!row.fullDeploymentTrigger) return '-';

        const action = row.isFullyDeployed ? 'Final deal' : 'Final deal undone';

        if (
          row.fullDeploymentTrigger === FundFullDeploymentTrigger.Allocation
        ) {
          if (!row.shareAllocationUploadId) {
            return `${action} - upload shareholding`;
          }
          return row.shareAllocationUploadId.deletedAt ? (
            <span>
              {getCompanyLabel(row.shareAllocationUploadId.companyId)}
            </span>
          ) : (
            <>
              {action} -{' '}
              <NavLink
                to={createViewAllocationRoute(row.shareAllocationUploadId._id)}
              >
                {getCompanyLabel(row.shareAllocationUploadId.companyId)}
              </NavLink>
            </>
          );
        }
        if (
          row.fullDeploymentTrigger === FundFullDeploymentTrigger.FundSettings
        ) {
          return `${action} - tranche page`;
        }
        if (
          row.fullDeploymentTrigger ===
          FundFullDeploymentTrigger.AllocationPreview
        ) {
          return `${action} - allocation preview`;
        }
      },
    },
    {
      label: 'Movement to/from cash balances',
      key: 'cashBalanceMovement',
      render: (row) =>
        numberToCurrencyString(row.cashBalanceMovement, { fallback: '-' }),
    },
  ];

  return (
    <Column spacing="lg" fullWidth>
      <FormField
        label="Tranche fully deployed (final deal completed)"
        layout="horizontal"
        labelSize="lg"
        tooltip={
          hasKiCertificate
            ? "The fully deployed status of this tranche cannot be changed while an EIS5 certificate for this tranche has been generated. Please navigate to the S/EIS Tasklist and delete this tranche's EIS5 certificate if you wish to use this toggle."
            : undefined
        }
      >
        <Switch
          checked={isFullyDeployed}
          onChange={
            isFullyDeployed
              ? undoDeploymentDisclosure.onOpen
              : fullyDeployDisclosure.onOpen
          }
          disabled={mutationInProgress || hasKiCertificate || !canEditTranche}
        />
      </FormField>

      <ConfirmDialog
        open={fullyDeployDisclosure.isOpen}
        onClose={fullyDeployDisclosure.onClose}
        title="Fully deploy tranche"
        content="Any residual cash in subscriptions will be returned to investor cash balances."
        actions={{
          confirm: {
            onClick: async () => {
              await fullyDeployFund.mutateAsync();
              fullyDeployDisclosure.onClose();
            },
            disabled: mutationInProgress || !canEditTranche,
          },
        }}
        loading={mutationInProgress}
      />

      <ConfirmDialog
        open={undoDeploymentDisclosure.isOpen}
        onClose={undoDeploymentDisclosure.onClose}
        title="Undo full deployment of tranche"
        content="Upon undoing this tranche's full deployment status, any investor residual cash, which has not since been withdrawn, will return to the tranche's deployable capital."
        actions={{
          confirm: {
            onClick: async () => {
              await undoFundDeployment.mutateAsync();
              undoDeploymentDisclosure.onClose();
            },
            disabled: mutationInProgress || !canEditTranche,
          },
        }}
        loading={mutationInProgress}
      />

      {logs.length > 0 && (
        <Table
          columns={columns}
          rows={logs}
          isLoading={fullyDeployedLog.isLoading}
          hidePaginationForSinglePage
        />
      )}
    </Column>
  );
};

export default FinalDeal;
